import { Card, CardBody, CardFooter, CardHeader, Chip } from '@heroui/react';
import { FaLayerGroup, FaStopwatch, FaCheck } from 'react-icons/fa6';
import CustomButton from '../../CustomButton';
import { formatCurrency } from '../../../../utils/currency';
import { PackageTier } from '../../../../utils/type';

interface PlanSummaryCardProps {
  tier: PackageTier;
  selected?: boolean;
  onSelect?: (tier: PackageTier) => void;
  buttonLabel?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
}

const PlanSummaryCard = ({
  tier,
  selected = false,
  onSelect,
  buttonLabel = 'Buy Package',
  isDisabled = false,
  isLoading = false,
}: PlanSummaryCardProps) => {
  const isMonthly = tier?.recurrence === 'monthly';

  const handleSelect = () => {
    if (isDisabled || isLoading) return;
    onSelect?.(tier);
  };

  return (
    <div
      onClick={handleSelect}
      role="button"
      tabIndex={isDisabled || isLoading ? -1 : 0}
      aria-pressed={selected}
      aria-disabled={isDisabled || isLoading}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSelect();
        }
      }}
      className={`relative w-full transition-all duration-200 rounded-2xl ${
        isDisabled || isLoading
          ? 'opacity-60 cursor-not-allowed'
          : 'cursor-pointer'
      }`}
    >
      <Card
        shadow="none"
        className={`relative border rounded-2xl w-full flex flex-col justify-between transition-all duration-200 ${
          selected
            ? 'border-primary ring-2 ring-primary/10 bg-blue-50/20 shadow-md'
            : 'border-slate-200 bg-white hover:border-primary/30 hover:shadow-lg'
        }`}
      >
        {/* Top right selection check indicator - only renders when selected */}
        {selected && (
          <div className="absolute top-3 right-3 w-5 h-5 bg-primary rounded-full flex items-center justify-center shadow-sm z-10">
            <FaCheck size={10} className="text-white" />
          </div>
        )}

        <div>
          <CardHeader className="flex items-start justify-between gap-2 pb-2 pt-4 px-4">
            <div className="flex flex-col pr-4">
              <h3 className="text-base font-bold text-slate-800 leading-snug">
                {tier?.name || 'Package'}
              </h3>
              {tier?.description ? (
                <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                  {tier.description}
                </p>
              ) : null}
            </div>
            {isMonthly && (
              <Chip
                size="sm"
                radius="full"
                color="primary"
                variant="flat"
                className="shrink-0 text-[10px] font-semibold uppercase tracking-wider"
              >
                Monthly
              </Chip>
            )}
          </CardHeader>

          <CardBody className="py-2 px-4">
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-extrabold text-slate-900">
                {formatCurrency(tier?.price)}
              </span>
            </div>

            <div className="flex flex-col gap-1.5 mt-3 pt-3 border-t border-slate-100">
              <span className="flex items-center gap-2 text-xs text-slate-600 font-medium">
                <FaLayerGroup size={12} className="text-blue-500 shrink-0" />
                {tier?.sessionCount} included appointments
              </span>
              {tier?.validityDays ? (
                <span className="flex items-center gap-2 text-xs text-slate-500">
                  <FaStopwatch size={12} className="text-slate-400 shrink-0" />
                  Valid for {tier.validityDays} days
                </span>
              ) : null}
            </div>
          </CardBody>
        </div>

        <CardFooter className="pt-2 pb-4 px-4">
          <CustomButton
            label={buttonLabel}
            color={selected ? 'primary' : 'default'}
            variant={selected ? 'solid' : 'bordered'}
            size="sm"
            fullWidth
            isDisabled={isDisabled}
            isLoading={isLoading}
            onPress={handleSelect}
            className={
              selected
                ? 'bg-primary text-white'
                : 'text-primary hover:border-secondary hover:bg-primary/5'
            }
          />
        </CardFooter>
      </Card>
    </div>
  );
};

export default PlanSummaryCard;
